import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios, { InternalAxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';
import * as microsoftTeams from '@microsoft/teams-js';
import { tokenRequest } from 'components/Auth/MSALAuthObjects';
import Network from 'PREPAREsrc/service/Network';
import { useBrowserAuth } from '../providers/BrowserAuthProvider';
import parseJWT from 'utils/parseJWT';

interface AuthState {
  type: string;
  accessToken?: string;
  expiresOn?: string;
  teamsInitialized: boolean;
  account?: any;
  msalInstance?: any;
}

const getTokenExpiration = (token: string, fallbackValue: string): string => {
  const parsedToken = parseJWT(token);
  const exp = parsedToken?.exp;

  if (typeof exp === 'number') {
    return new Date(exp * 1000).toISOString();
  }

  if (typeof exp === 'string' && !Number.isNaN(Number(exp))) {
    return new Date(Number(exp) * 1000).toISOString();
  }

  if (typeof parsedToken?.expiresOn === 'string') {
    return parsedToken.expiresOn as string;
  }

  return fallbackValue;
};

const applyAccessToken = (
  config: InternalAxiosRequestConfig,
  accessToken: string
): InternalAxiosRequestConfig => {
  config.headers.Authorization = `Bearer ${accessToken}`;
  Network.http.defaults.headers.common['Authorization'] =
    `Bearer ${accessToken}`;
  return config;
};

/**
 * Sets up an axios request interceptor that handles auth token injection,
 * refresh, and content-type headers for all outgoing requests.
 */
export function useAxiosInterceptor(): void {
  const { getAccessToken, login } = useBrowserAuth();

  const auth: AuthState = useSelector((state: any) => state.auth);

  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      async (config) => {
        // Default settings
        (config as any).credentials = 'same-origin';
        config.headers.Accept = 'application/json';
        const fromDataUrl = ['uploadFile', 'bulkUserImport'];

        if (
          config.data instanceof FormData ||
          fromDataUrl.includes(config.url!.split('?')[0].split('/').pop()!)
        ) {
          config.headers['Content-Type'] = 'multipart/form-data';
        } else {
          config.headers['Content-Type'] = 'application/json';
        }

        // Context (Teams vs non-Teams)
        const isTeams =
          sessionStorage['isUsingTeams'] === 'true' || auth.teamsInitialized;
        const teamsHeaderValue = isTeams ? 'true' : 'false';
        config.headers['x-teamsapp'] = teamsHeaderValue;
        Network.http.defaults.headers.common['x-teamsapp'] = teamsHeaderValue;

        // Auth refresh
        const now = dayjs();
        const expiresOn = dayjs(sessionStorage['accessTokenExpiresOn']);
        const isTokenValid = expiresOn.diff(now.add(1, 'minute')) > 0;
        const isBrowserProviderAuth =
          auth.type === 'AUTH0' || auth.type === 'DESCOPE';

        const getBrowserProviderToken = async () => {
          const accessToken = await getAccessToken();

          if (!accessToken) {
            throw new Error('Missing browser auth access token');
          }

          return {
            accessToken,
            expiresOn: getTokenExpiration(
              accessToken,
              now.add(1, 'hour').toISOString()
            ),
          };
        };

        if (isTokenValid) {
          // Token still valid – use existing token
          if (isTeams) {
            // Teams/Outlook: always use the Entra SSO token from sessionStorage,
            // even if auth.type is stale from a prior Auth0 render.
            const teamsToken = sessionStorage['accessToken'];
            if (!teamsToken) {
              return Promise.reject(
                new Error('Teams SSO token missing from sessionStorage')
              );
            }
            return applyAccessToken(config, teamsToken);
            // Token still valid – use existing token
          } else if (isBrowserProviderAuth) {
            try {
              const browserAuthToken = await getBrowserProviderToken();
              return applyAccessToken(config, browserAuthToken.accessToken);
            } catch (error) {
              console.error('Error getting browser auth token:', error);
              await login();
              return Promise.reject(error);
            }
          } else {
            // MSAL or Teams (valid token path)
            return applyAccessToken(config, sessionStorage['accessToken']);
          }
        } else {
          // Token has expired; attempt to refresh or re-acquire

          // Teams SSO: tokens cannot be refreshed silently; must be re-acquired
          if (isTeams) {
            const newToken = await microsoftTeams.authentication.getAuthToken();

            sessionStorage['accessToken'] = newToken;
            sessionStorage['accessTokenExpiresOn'] = getTokenExpiration(
              newToken,
              now.add(1, 'hour').toISOString()
            );

            return applyAccessToken(config, newToken);
          }

          let refreshTokenResponse;
          if (auth.type === 'MSAL') {
            refreshTokenResponse = await auth.msalInstance.acquireTokenSilent({
              ...tokenRequest,
              account: auth.account,
            });
            sessionStorage['accessToken'] = refreshTokenResponse.accessToken;
            sessionStorage['accessTokenExpiresOn'] =
              refreshTokenResponse.expiresOn;
            return applyAccessToken(config, refreshTokenResponse.accessToken);
          } else if (isBrowserProviderAuth) {
            try {
              refreshTokenResponse = await getBrowserProviderToken();
              sessionStorage['accessToken'] = refreshTokenResponse.accessToken;
              sessionStorage['accessTokenExpiresOn'] =
                refreshTokenResponse.expiresOn;
              return applyAccessToken(config, refreshTokenResponse.accessToken);
            } catch (error) {
              console.error('Error refreshing browser auth token:', error);
              await login();
              return Promise.reject(error);
            }
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [
    auth.type,
    getAccessToken,
    login,
    auth.account,
    auth.msalInstance,
    auth.teamsInitialized,
  ]);
}
